import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { photoUrl } from "../../lib/helpers";
import { VoiceInput } from "./VoiceInput";
import { ReferenceUpload } from "./ReferenceUpload";
import { useAiCtx } from "./aiContext";

function PhotoPick({ projectId, sel, setSel }) {
  const [photos, setPhotos] = useState([]);
  useEffect(() => { if (projectId) api.get(`/projects/${projectId}`).then((r) => setPhotos((r.data.photos || []).filter((p) => p.media_type !== "video").slice(0, 24))).catch(() => {}); }, [projectId]);
  if (!photos.length) return null;
  const tog = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : sel.length < 4 ? [...sel, id] : sel);
  return <div className="flex gap-2 overflow-x-auto pb-1" data-testid="autofill-photos">{photos.map((p) => <button type="button" key={p.id} onClick={() => tog(p.id)} className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${sel.includes(p.id) ? "border-[#D9531E]" : "border-transparent"}`} data-testid={`autofill-photo-${p.id}`}><img src={photoUrl(p.id)} alt="" className="w-full h-full object-cover" /></button>)}</div>;
}

function Preview({ res, labels, rows, setRows }) {
  const { t } = useLang();
  return (
    <div className="grid gap-2" data-testid="autofill-preview">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("Autofill preview", "Vista previa del autollenado")}</div>
      {res.comment && <p className="text-sm text-slate-600">{res.comment}</p>}
      {Object.keys(rows).map((k) => {
        const money = res.money_fields.includes(k);
        const long = String(rows[k].v).length > 60;
        return (
          <div key={k} className={`rounded-xl border p-2 grid gap-1 ${money ? "border-amber-300 bg-amber-50" : "border-slate-200"}`} data-testid={`autofill-row-${k}`}>
            <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={rows[k].on} onChange={(e) => setRows({ ...rows, [k]: { ...rows[k], on: e.target.checked } })} data-testid={`autofill-check-${k}`} /> {labels[k] || k}{money && <span className="text-[10px] font-bold text-amber-700">{t("AI SUGGESTED AMOUNT – verify", "MONTO SUGERIDO POR IA – verifique")}</span>}</label>
            {long ? <textarea className="inp !min-h-[70px] text-sm" value={rows[k].v} onChange={(e) => setRows({ ...rows, [k]: { ...rows[k], v: e.target.value } })} data-testid={`autofill-value-${k}`} />
              : <input className="inp text-sm" value={rows[k].v} onChange={(e) => setRows({ ...rows, [k]: { ...rows[k], v: e.target.value } })} data-testid={`autofill-value-${k}`} />}
          </div>
        );
      })}
    </div>
  );
}

export function AutofillButton({ form, current, onApply, labels = {}, ctx, title, className = "" }) {
  const { t, lang } = useLang();
  const { can, isStaff } = useAuth();
  const c = useAiCtx(ctx);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [refs, setRefs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [rows, setRows] = useState({});
  if (!isStaff || !can("ai_assistant")) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/autofill", { form, text, current, language: lang, reference_ids: refs.map((r) => r.id), photo_ids: photos, ...c });
      if (!Object.keys(data.fields).length) return toast.error(t("The AI found nothing to fill. Add more details.", "La IA no encontró qué llenar. Agregue más detalles."));
      setRes(data);
      setRows(Object.fromEntries(Object.entries(data.fields).map(([k, v]) => [k, { v: String(v), on: !data.money_fields.includes(k) }])));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const apply = (all) => {
    const out = Object.fromEntries(Object.entries(rows).filter(([, r]) => all || r.on).map(([k, r]) => [k, typeof res.fields[k] === "number" ? Number(r.v) || 0 : r.v]));
    onApply(out);
    toast.success(t("Fields filled. You can still edit everything.", "Campos llenados. Puede seguir editando todo."));
    close();
  };
  const close = () => { setOpen(false); setRes(null); };
  return (
    <>
      <button type="button" className={`btn-outline btn-sm !border-orange-200 !text-[#D9531E] ${className}`} onClick={() => setOpen(true)} data-testid={`autofill-${form}-btn`}><Sparkles className="w-4 h-4" /> {title || t("Autofill with AI", "Autollenar con IA")}</button>
      {open && <Dialog open onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="autofill-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D9531E]" /> {title || t("Autofill with AI", "Autollenar con IA")}</DialogTitle></DialogHeader>
          {!res ? (<div className="grid gap-3">
            <div className="flex gap-2"><textarea className="inp !min-h-[110px] flex-1" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("Explain the job once: what, where, materials, price, warranty…", "Explique el trabajo una vez: qué, dónde, materiales, precio, garantía…")} data-testid="autofill-text" /><VoiceInput onText={(s) => setText((x) => `${x} ${s}`.trim())} testid="autofill-voice" /></div>
            <ReferenceUpload refs={refs} setRefs={setRefs} testid="autofill-ref" />
            <PhotoPick projectId={c.project_id} sel={photos} setSel={setPhotos} />
            <p className="text-xs text-slate-500">{t("Uses the customer/project data you can see, work recipes and company settings. Nothing is saved until you apply and save the form.", "Usa los datos del cliente/proyecto que usted puede ver, las recetas y la configuración de la empresa. Nada se guarda hasta que aplique y guarde el formulario.")}</p>
            <button className="btn-primary h-12" disabled={busy} onClick={run} data-testid="autofill-run-btn">{busy ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("Preparing…", "Preparando…")}</> : t("Prepare fields", "Preparar campos")}</button>
          </div>) : (<div className="grid gap-3">
            <Preview res={res} labels={labels} rows={rows} setRows={setRows} />
            <div className="grid grid-cols-2 gap-2 sticky bottom-0 bg-white pt-2">
              <button className="btn-primary" onClick={() => apply(false)} data-testid="autofill-apply-selected">{t("Apply selected", "Aplicar seleccionados")}</button>
              <button className="btn-dark" onClick={() => apply(true)} data-testid="autofill-apply-all">{t("Apply all", "Aplicar todos")}</button>
              <button className="btn-outline" onClick={() => setRes(null)} data-testid="autofill-edit-ai">{t("Edit with AI", "Editar con IA")}</button>
              <button className="btn-ghost" onClick={close} data-testid="autofill-cancel">{t("Cancel", "Cancelar")}</button>
            </div>
          </div>)}
        </DialogContent>
      </Dialog>}
    </>
  );
}
